// Dialog de criação/edição de journey Zenvia — RHF + Zod: evento
// (select com os 21 do catálogo de constantes), canal, journeyId
// obrigatório e versionId opcional. O write é otimista
// (provider-mutations), então o dialog fecha já no mutate. Suporta
// pré-preenchimento (chips de eventos ativos sem journey).
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type {
  NotificationSearchChannelType,
  NotificationZenviaJourneyType,
} from "@/types/notification-center"

import {
  NOTIFICATION_EVENTS,
  NOTIFICATION_SEARCH_CHANNELS,
} from "../constants"
import {
  useCreateZenviaJourney,
  useUpdateZenviaJourney,
} from "../provider-mutations"

const journeyFormPrefix =
  "pages.notificationCenter.providersPage.journeyForm"

// Schema do form — evento/canal/journeyId obrigatórios; versionId
// opcional (o backend assume a versão ativa da journey).
const buildJourneyFormSchema = (requiredMessage: string) =>
  z.object({
    event: z.string().min(1, requiredMessage),
    channel: z.enum(NOTIFICATION_SEARCH_CHANNELS),
    journeyId: z.string().min(1, requiredMessage),
    versionId: z.string(),
  })
type JourneyFormValues = z.infer<
  ReturnType<typeof buildJourneyFormSchema>
>

// Pré-preenchimento vindo do alerta de eventos não mapeados.
export type ZenviaJourneyPrefill = {
  event: string
  channel: NotificationSearchChannelType
}

type ZenviaJourneyFormDialogProps = {
  whiteLabelId: string
  // Presente = edição; ausente = criação.
  journey?: NotificationZenviaJourneyType
  prefill?: ZenviaJourneyPrefill
  onClose: () => void
}

export const ZenviaJourneyFormDialog = ({
  whiteLabelId,
  journey,
  prefill,
  onClose,
}: ZenviaJourneyFormDialogProps) => {
  const { t } = useTranslation()
  const isEdit = Boolean(journey)
  const createJourney = useCreateZenviaJourney(whiteLabelId)
  const updateJourney = useUpdateZenviaJourney(whiteLabelId)

  const form = useForm<JourneyFormValues>({
    resolver: zodResolver(
      buildJourneyFormSchema(
        t(`${journeyFormPrefix}.required`)
      )
    ),
    defaultValues: {
      event: journey?.event ?? prefill?.event ?? "",
      channel:
        journey?.channel ?? prefill?.channel ?? "WHATSAPP",
      journeyId: journey?.journeyId ?? "",
      versionId: journey?.versionId ?? "",
    },
  })

  const handleSubmit = form.handleSubmit((values) => {
    const payload = {
      event: values.event,
      channel: values.channel,
      journeyId: values.journeyId,
      // versionId vazio é OMITIDO do payload (opcional no contrato).
      ...(values.versionId
        ? { versionId: values.versionId }
        : {}),
    }
    // Rótulo pt-BR p/ o cache otimista (o ack estático não devolve).
    const eventLabel =
      NOTIFICATION_EVENTS.find(
        (event) => event.value === values.event
      )?.label ?? values.event
    if (isEdit && journey) {
      updateJourney.mutate({
        journeyRecordId: journey.id,
        payload,
        eventLabel,
      })
    } else {
      createJourney.mutate({ payload, eventLabel })
    }
    // Write otimista: fecha já — erro faz rollback + toast.
    onClose()
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(
              `${journeyFormPrefix}.${isEdit ? "editTitle" : "createTitle"}`
            )}
          </DialogTitle>
          <DialogDescription>
            {t(`${journeyFormPrefix}.description`)}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="event"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${journeyFormPrefix}.event`)}
                  </FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger
                        aria-label={t(
                          `${journeyFormPrefix}.event`
                        )}
                      >
                        <SelectValue
                          placeholder={t(
                            `${journeyFormPrefix}.eventPlaceholder`
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {NOTIFICATION_EVENTS.map((event) => (
                        <SelectItem
                          key={event.value}
                          value={event.value}
                        >
                          {event.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="channel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${journeyFormPrefix}.channel`)}
                  </FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger
                        aria-label={t(
                          `${journeyFormPrefix}.channel`
                        )}
                      >
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {NOTIFICATION_SEARCH_CHANNELS.map(
                        (channel) => (
                          <SelectItem
                            key={channel}
                            value={channel}
                          >
                            {channel}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="journeyId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${journeyFormPrefix}.journeyId`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="font-mono"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="versionId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${journeyFormPrefix}.versionId`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="font-mono"
                      placeholder={t(
                        `${journeyFormPrefix}.versionIdPlaceholder`
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                {t(`${journeyFormPrefix}.cancel`)}
              </Button>
              <Button type="submit">
                {t(`${journeyFormPrefix}.save`)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
